/**
 * Clip-revamp feature flag module (PR-1 scaffolding).
 *
 * Source of truth:
 * - FEATURE_PLAN.md §13.2 (line ~2136) — exports and semantics.
 * - FEATURE_PLAN.md §13.5 (line ~2230) — call-site function shape.
 * - FEATURE_PLAN.md §16.2 (line ~2446) — EU cookie gate (`rc_clip_gate=off`)
 *   set by `src/middleware/edge.ts`.
 * - FEATURE_PLAN.md §4.2 (line ~307) — `CLIP_CAPTURE_PRESERVE_DRAWING_BUFFER`
 *   correctness fallback, imported by `src/renderer/setup.ts`.
 *
 * The const is the universal kill-switch (code-reviewable); the cookie can
 * only force an already-`true` flag to `false` for EU users. The server can
 * only turn the feature **off**; it cannot turn it on.
 */

/**
 * Client-side kill-switch. Flip to `false` in a 1-line PR to disable the
 * clip-revamp pipeline everywhere (see §14 rollback plan).
 */
export const CLIP_REVAMP_ENABLED_CONST: boolean = true;

/**
 * §4.2 correctness fallback. Starts `false` (Pixi default) to avoid the
 * ~5–10% GPU-memory overhead `preserveDrawingBuffer: true` imposes on iOS.
 * Flipped `true` only if Sentry shows the `renderer.extract` path failing
 * (`encoderPath: 'capture-black-frames'`).
 *
 * Imported by `src/renderer/setup.ts` and passed to `app.init()`.
 */
export const CLIP_CAPTURE_PRESERVE_DRAWING_BUFFER: boolean = false;

// Per §13.2: "Cached once per module load (EU cookie is set at
// page-HTML-response time by src/middleware/edge.ts per §16.2 and doesn't
// change within a session)." SSR-safe: treat absent `document` as "no
// cookie", which means EU gate has no effect in non-browser contexts.
const EU_COOKIE_GATE_ACTIVE: boolean =
  typeof document !== 'undefined' &&
  document.cookie.includes('rc_clip_gate=off');

/**
 * Call-site API. Returns `true` when the clip revamp feature should be
 * enabled for this session. Combines the client-side const with the
 * `rc_clip_gate=off` EU cookie (set by Vercel Edge middleware per §16.2).
 *
 * Per §13.5, the call-site is a function (not a const) so the EU cookie
 * can veto. The cookie read is cached once per module load.
 */
export function CLIP_REVAMP_ENABLED(): boolean {
  return CLIP_REVAMP_ENABLED_CONST && !EU_COOKIE_GATE_ACTIVE;
}
